import type { ReactNode } from "react";
import { Card, CardContent } from "../../card";
import { Icon } from "../../icon";
import { PulseSynthesis } from "./pulse-synthesis";
import { Pulse } from "./pulse-wall";
import { PulseClaims, type PulseClaimMark } from "./pulse-claims";
import { FocusSections, type FocusSectionsProps } from "../dashboard/focus-sections";

/** A committed bet riding a wall cluster (from `buildPulseSpine(scope).claims`). */
export interface SpineClaim {
  mode: string;
  groupKey: string;
  label: string;
}

/** Mode wall (sections): mark clusters whose `group.key` a bet in THIS mode rides. */
function modeMarks(claims: SpineClaim[] | undefined, scope: string): PulseClaimMark[] {
  return (claims ?? []).filter((c) => c.mode === scope).map((c) => ({ key: c.groupKey, label: c.label }));
}
/** Home wall (mode clusters): mark each mode any bet rides (deduped, first label). */
function workspaceMarks(claims: SpineClaim[] | undefined): PulseClaimMark[] {
  const byMode = new Map<string, string>();
  for (const c of claims ?? []) if (!byMode.has(c.mode)) byMode.set(c.mode, c.label);
  return [...byMode].map(([key, label]) => ({ key, label }));
}

/**
 * PulseSpine — the ONE composed measured surface (docs/design/PULSE-SPINE-PRD.md).
 * The bands, top to bottom, on one stage:
 *
 *   ① READ    the synthesis, sitting directly ON the wall (shared stage → its
 *             phrases wire to the bars). Reuses `PulseSynthesis` verbatim.
 *   ③ WALL    the pulse instrument. Reuses `Pulse` verbatim (passed as the
 *             synthesis's children so read + wall never remount apart).
 *   ④ ACT     the per-domain response. Reuses `FocusSections` verbatim.
 *   ② COMMIT  the dated bets. App-wide-sourced, locally relevant: each surface
 *             shows only the bets riding ITS bars, as a ◎ (the `PulseClaims`
 *             overlay over the cluster a `kr.signal_ref` binds — v91). The full bet
 *             LIST stays on the home (the `GranularPulse` children carry
 *             `ObjectivesHero`); mode dashboards get the ◎, not a repeated strip.
 *
 * This block is PURE COMPOSITION — it re-implements nothing (no forked wall, no
 * forked connector engine; the real wiring lives in `PulseSynthesisView`). It
 * replaces the three scattered mounts (mode-page `pulseNode` ladder, the
 * `FocusAndHealth` body, the home read+objectives+focus stack) with one node.
 *
 * `{read, wall}` are client SWR reads (PulseSynthesis/Pulse fetch `pulse.aggregate`
 * by the SAME key) — the server only threads `scope`/`projectSlug`. `{act}` is
 * server-assembled by `buildPulseSpine` (passed in as `act`). See the assembler.
 */

export type SpineAltitude = "aggregate-mode" | "aggregate-workspace" | "strip" | "off";

export interface PulseSpineProps {
  altitude: SpineAltitude;
  /** The pulse scope (mode slug) — the SWR key the read + wall share. */
  scope: string;
  /** The surface's display label (nav/title prototype). The read renders as an
   *  editorial SPLIT HEADER — title in the brand display face on the left, the
   *  read as fixed-height body text on the right — above the wall. On the
   *  `aggregate-mode` root pass the mode label; on a `strip` sub-page pass the
   *  LEAF label (the domain / lane / nested-shell name — the same value the
   *  ModeShell `mode` prop carries). Home keeps its classic read. */
  title?: string;
  /** The tiny letterspaced eyebrow under the title (the mode's question). */
  eyebrow?: string;
  /**
   * Tighten the title's vertical rhythm for a SECTION / drilled surface (a domain
   * / lane / nested / `?area` page) vs a mode-root wall hero. Since the one serif
   * title treatment (Matt, 2026-07-16 — supersedes the Issue-7 editorial/compact
   * split), this NO LONGER changes the title face or size — mode roots and
   * sections render the same 28px `.type-page-title`; `compact` only adjusts the
   * gap beneath the title.
   */
  compact?: boolean;
  /** The section's OWN primary CTA + filters, inline on the right of the title row. */
  titleToolbar?: ReactNode;
  projectSlug?: string | null;
  /** On a `strip`, the active `?area` band to ring (you-are-here). */
  current?: string;
  /** ④ ACT data from `buildPulseSpine(scope).act` — omit on a strip. */
  act?: { groups: FocusSectionsProps["groups"]; crosscutting?: FocusSectionsProps["crosscutting"] };
  /** ② ◎ — `buildPulseSpine(scope).claims`; marks the bars a committed bet rides. */
  claims?: SpineClaim[];
  /**
   * Suppress ④ entirely (no FocusSections, no all-clear card). For a surface
   * that carries its OWN response bands below the spine — the redesigned Home,
   * whose "Next" + "Signals" sections replace ④ — where the all-clear card
   * would sit dishonestly above a Next card saying something DOES need you.
   */
  hideAct?: boolean;
  /**
   * On `aggregate-workspace`, the read+wall(+objectives) node (the app's
   * `GranularPulse`) — passed as children because the cross-mode read lives in the
   * app, not the UI package. The block appends ④ below it.
   */
  children?: ReactNode;
  /**
   * Override the wall inside the synthesis stage (`aggregate-mode` / `strip`). The
   * self-navigating bar (`PulseNavShell`) slots in here so it keeps the editorial
   * split header + ② ◎ claims + ④ act — only the wall (③) is swapped. Omit for the
   * default `<Pulse>` wall. See docs/lifecycle-spine/SELF-NAV-ROLLOUT.md.
   */
  wall?: ReactNode;
}

export function PulseSpine({ altitude, scope, title, eyebrow, compact, titleToolbar, projectSlug, current, act, claims, hideAct = false, children, wall }: PulseSpineProps) {
  if (altitude === "off") return null;

  if (altitude === "strip") {
    // Sub-page context: the quiet-spine wall WITH a band-focused read so every
    // page reachable from the Pulse carries its own insight. `focus={current}`
    // narrows the read to the band this page drilled into (its signals, its drag,
    // its fix) — not a repeat of the mode-root read. The read re-animates on nav
    // (PulseSynthesis re-keys on scope+focus). `title` (the LEAF label) renders
    // the same split header as the mode root — title left, fixed-height read
    // right, the strip wall below. No ④ act here — the page owns its body.
    return (
      <PulseSynthesis
        scope={scope}
        title={title}
        compact={compact}
        titleToolbar={titleToolbar}
        projectSlug={projectSlug ?? undefined}
        focus={current}
      >
        {wall ?? <Pulse size="strip" scope={scope} current={current} projectSlug={projectSlug ?? undefined} />}
      </PulseSynthesis>
    );
  }

  if (altitude === "aggregate-workspace") {
    // The home: the workspace read+wall+objectives ride in `children` (GranularPulse);
    // the block appends ④ ACT below — one composed surface, one mount. ② ◎ marks the
    // MODE clusters any committed bet rides (the full bet list stays in GranularPulse).
    return (
      <div className="flex flex-col gap-5">
        <PulseClaims claims={workspaceMarks(claims)}>{children}</PulseClaims>
        {hideAct ? null : <SpineAct act={act} />}
      </div>
    );
  }

  // aggregate-mode — the mode dashboard root: read on wall (+ ② ◎) + ④ response.
  // No objectives STRIP here (objectives are app-wide, not repeated per page); each
  // bet shows only where it rides — as a ◎ on the section it binds (kr.signal_ref).
  return (
    <div className="flex flex-col gap-5">
      {/* ① READ on ③ WALL — one shared stage (PulseSynthesis wraps the wall); ② ◎
          rides the claimed section cards via the PulseClaims overlay. With a
          `title`, ① becomes the editorial SPLIT HEADER (nav/title prototype):
          mode name left · read right, wall below — same stage, same wiring. */}
      <PulseSynthesis scope={scope} title={title} eyebrow={eyebrow} projectSlug={projectSlug ?? undefined}>
        <PulseClaims claims={modeMarks(claims, scope)}>
          {wall ?? <Pulse size="full" scope={scope} projectSlug={projectSlug ?? undefined} />}
        </PulseClaims>
      </PulseSynthesis>

      {children}

      {/* ④ ACT — the per-domain response, or a calm all-clear. */}
      {hideAct ? null : <SpineAct act={act} />}
    </div>
  );
}

/** ④ — FocusSections, or the moved "all clear" card (never a blank frame). */
function SpineAct({ act }: { act?: PulseSpineProps["act"] }) {
  if (!act || (act.groups.length === 0 && !act.crosscutting)) {
    return (
      <Card className="gap-0 overflow-hidden py-0">
        <CardContent className="flex items-center gap-3 px-5 py-4">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted/20">
            <Icon name="circle-check" size="sm" className="text-success/80" />
          </span>
          <div className="min-w-0">
            <div className="type-body font-medium text-foreground">Nothing needs you here</div>
            <div className="type-body text-muted-foreground">
              Everything in view is on track — the Pulse stays your live read.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  return <FocusSections groups={act.groups} crosscutting={act.crosscutting} />;
}
